/**
 * MML raster-proxy capability store (renderer-safe half).
 *
 * The FastAPI tile proxy 503s every tile request when the MML api-key /
 * WMTS base URL are not configured (CI, keyless local dev). Probing with
 * tile requests floods the browser console with network errors before the
 * tileerror→OSM fallback engages, so basemap components instead read this
 * store, populated once at bootstrap by `initialiseMmlTileAvailability`
 * (`mmlTileAvailabilityInit.ts`, called from `main.tsx` — renderers never
 * start server requests, per the route-purity contract).
 *
 * The runtime tileerror fallback in the basemap components stays — this
 * store only removes the guaranteed-to-fail probe path.
 */
import { useSyncExternalStore } from 'react';

type Availability = boolean | null;

let availability: Availability = null;
const listeners = new Set<() => void>();

export const setMmlTilesAvailable = (value: boolean): void => {
  availability = value;
  for (const listener of listeners) listener();
};

/** Test-only: reset the session state between test cases. */
export const resetMmlTileAvailabilityForTests = (): void => {
  availability = null;
  for (const listener of listeners) listener();
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = (): Availability => availability;

/**
 * True/false when the proxy answered the capability probe authoritatively
 * (200 with an `available` verdict); null while the check is in flight or
 * when the probe was inconclusive (non-ok, network error, timeout) — see
 * `initialiseMmlTileAvailability`.
 */
export const useMmlTilesAvailable = (): Availability =>
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

export type BasemapMode = 'mml' | 'osm';

/**
 * Pure capability-gating decision shared by every basemap component
 * (`FmMmlBasemapTiles`, ops-map `MmlTiles`). The JSX stays local to each
 * component (route-purity), only the decision logic is shared.
 *
 * - availability `true` → MML, even after a tileerror. The proxy is
 *   configured, so a failed tile is transient (the proxy's 429 budget, an
 *   upstream timeout): the basemap requests it again after the proxy's
 *   admission window and reports any uncovered area as a raster gap (to the
 *   map's owner, or as its own map notice), never a switch to direct
 *   tile.openstreetmap.org traffic, which the OSMF tile usage policy forbids
 *   at production volume.
 * - `useFallback` (a tileerror fired while availability is unknown) → OSM.
 * - availability `false` → OSM directly, skipping guaranteed-503 MML probes.
 * - availability `null` (capability check still in flight, probe was
 *   inconclusive — non-ok/network-error/timeout — or bootstrap never ran, as
 *   in unit tests and isolated component harnesses) → render MML
 *   optimistically; the runtime tileerror→OSM fallback still protects the
 *   map, exactly as before capability discovery existed. This does NOT
 *   reintroduce the doomed-probe console burst in keyless CI: the
 *   availability fetch fires at module load in `main.tsx` while every map
 *   route sits behind auth navigation, so availability resolves to `false`
 *   long before any map route can mount a TileLayer.
 */
export const resolveBasemapMode = (
  available: Availability,
  useFallback: boolean,
): BasemapMode => {
  if (available === true) return 'mml';
  return useFallback || available === false ? 'osm' : 'mml';
};
